import Icon from '@ant-design/icons';
import { Modal, Spin, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import { formatCurrency } from '../../library/util';
import { Class } from '../../models/Class';
import { Mode } from '../../models/Mode';
import { ShipType } from '../../models/ShipType';
import type { RateHistoryRecord } from '../../services/rateService';
import rateService from '../../services/rateService';
import NoFuelSurchargeSvg from '../icons/NoFuelSurchargeSvg';

interface RateHistoryModalProps {
  carrierClasses?: Class[];
  modes?: Mode[];
  onClose: () => void;
  open: boolean;
  rateId: number;
  shipTypes?: ShipType[];
}

const RateHistoryModal: React.FC<RateHistoryModalProps> = ({
  carrierClasses = [],
  modes = [],
  onClose,
  open,
  rateId,
  shipTypes = [],
}) => {
  const { data: history, isLoading } = useQuery<RateHistoryRecord[]>(
    ['rateHistory', rateId],
    () => rateService.getHistory(rateId),
    { enabled: open && !!rateId }
  );

  const classColumns = useMemo(() => {
    return (carrierClasses ?? []).map((c) => ({
      key: `class-${c.id}`,
      render: (_: unknown, record: RateHistoryRecord) => {
        const rateClasses = record.metadataJson?.history?.rateClasses ?? [];
        const rateClass = rateClasses.find((r) => r.classId === c.id);

        return rateClass?.value != null ? formatCurrency(rateClass.value) : '—';
      },
      title: c.name,
      width: 100,
    }));
  }, [carrierClasses]);

  const getRate = (record: RateHistoryRecord) =>
    record.metadataJson?.history?.rate as Record<string, unknown> | undefined;

  const columns: ColumnsType<RateHistoryRecord> = [
    {
      key: 'origin',
      render: (_: unknown, record: RateHistoryRecord) =>
        getRate(record)?.origin ?? '—',
      title: 'Origin',
      width: 140,
    },
    {
      key: 'originType',
      render: (_: unknown, record: RateHistoryRecord) => {
        const shipTypeId = getRate(record)?.shipTypeId as number | undefined;
        return shipTypeId != null
          ? shipTypes.find((st) => st.id === shipTypeId)?.name ?? '—'
          : '—';
      },
      title: 'Orig Type',
      width: 100,
    },
    {
      key: 'destination',
      render: (_: unknown, record: RateHistoryRecord) =>
        getRate(record)?.destination ?? '—',
      title: 'Destination',
      width: 140,
    },
    {
      key: 'destType',
      render: (_: unknown, record: RateHistoryRecord) => {
        const destShipTypeId = getRate(record)?.destinationShipTypeId as
          | number
          | undefined;
        return destShipTypeId != null
          ? shipTypes.find((st) => st.id === destShipTypeId)?.name ?? '—'
          : '—';
      },
      title: 'Dest Type',
      width: 100,
    },
    {
      key: 'mode',
      render: (_: unknown, record: RateHistoryRecord) => {
        const modeId = getRate(record)?.modeId as number | undefined;
        return modeId != null
          ? modes.find((m) => m.id === modeId)?.name ?? '—'
          : '—';
      },
      title: 'Mode',
      width: 100,
    },
    ...classColumns,
    {
      key: 'skipFuelSurcharge',
      render: (_: unknown, record: RateHistoryRecord) => {
        const skip = getRate(record)?.skipFuelSurcharge;
        if (skip === undefined || skip === null) return '—';
        if (!skip) return '';
        return (
          <Icon component={NoFuelSurchargeSvg} style={{ color: '#faad14' }} />
        );
      },
      title: (
        <Tooltip title="No fuel surcharge">
          <Icon component={NoFuelSurchargeSvg} />
        </Tooltip>
      ),
      width: 30,
    },
    {
      dataIndex: 'updatedAt',
      key: 'date',
      render: (val: string) =>
        val ? moment(val).format('MM/DD/YYYY, hh:mm A') : '—',
      title: 'Date Updated',
      width: 120,
    },
    {
      key: 'user',
      render: (_, record) => record.User?.firstName ?? '—',
      title: 'User',
      width: 100,
    },
  ];

  return (
    <Modal
      centered
      destroyOnClose
      footer={null}
      open={open}
      title={`Rate History: #${rateId}`}
      width={'90vw'}
      onCancel={onClose}
    >
      {isLoading ? (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={history ?? []}
          footer={() => (
            <Typography.Text style={{ fontSize: 12 }}>
              All prices shown are base rate (excl. fuel surcharge)
            </Typography.Text>
          )}
          pagination={{ pageSize: 5 }}
          rowKey="id"
          size="middle"
        />
      )}
    </Modal>
  );
};

export default RateHistoryModal;
